import i18next from 'i18next';
import { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import type { SvaraAchievementFamilyDto } from '../../services/api';
import { getSocket } from '../../services/socket';
import { RootState } from '../../store';
import { achievementsHubRequest } from '../../store/achievements/achievementsActions';
import {
	selectAchievementFamilies,
	selectAchievementsHubErrorKey,
	selectAchievementsHubLoading,
	selectAchievementsLoaded,
} from '../../store/achievements/achievementsReducer';
import { selectIsLogged } from '../../store/auth/authReducer';
import { fillTranslationPlaceholders } from '../../utils/helpers/globalBroadcastMessage';
import { showAchievementUnlockedToast } from './showGameBroadcastToast';

const LOAD_RETRY_BASE_MS = 30000;
const LOAD_RETRY_MAX_MS = 300000;
const HAND_HOLD_LIMIT_MS = 60000;
const UNLOCKED_EVENT = 'achievementUnlocked';
const HAND_START_EVENTS = new Set(['dealCards']);
const HAND_END_EVENTS = new Set(['showdown', 'splitSvaraPot', 'resetTable', 'exitTable']);
const UNLOCKED_TOAST_KEY = 'T_SITE_ACHIEVEMENTS_UNLOCKED';
const UNLOCKED_TOAST_DEFAULT = 'Achievement unlocked: %%';

type UnlockedEntry = { key: string; nameKey: string; displayTier: number };

const toUnlockedEntries = (raw: unknown): UnlockedEntry[] => {
	const achievements = (raw as { achievements?: unknown } | null)?.achievements;
	if (!Array.isArray(achievements)) return [];

	return achievements.filter(
		(entry): entry is UnlockedEntry =>
			typeof entry?.key === 'string' &&
			typeof entry?.nameKey === 'string' &&
			typeof entry?.displayTier === 'number',
	);
};

const showUnlockedToast = (
	entries: UnlockedEntry[],
	families: readonly SvaraAchievementFamilyDto[],
) => {
	const [first] = entries;
	if (!first) return;

	const names = entries.map(entry => i18next.t(entry.nameKey)).join(', ');
	const template = i18next.t(UNLOCKED_TOAST_KEY, { defaultValue: UNLOCKED_TOAST_DEFAULT });
	const iconKey = families
		.flatMap(family => family.tiers)
		.find(tier => tier.key === first.key)?.iconKey;

	showAchievementUnlockedToast(
		fillTranslationPlaceholders(template, [names]),
		first.displayTier,
		iconKey,
	);
};

const AchievementsHost = () => {
	const dispatch = useDispatch();
	const isLogged = useSelector(selectIsLogged);
	const loaded = useSelector(selectAchievementsLoaded);
	const hubLoading = useSelector(selectAchievementsHubLoading);
	const hubErrorKey = useSelector(selectAchievementsHubErrorKey);
	const families = useSelector(selectAchievementFamilies);
	// Read at toast time, so a catalog reload does not resubscribe the socket listener.
	const familiesRef = useRef(families);
	familiesRef.current = families;
	const user = useSelector((s: RootState) => s.auth?.user);
	const playerId = user?.id || '';
	const username = user?.username || '';
	const [loadAttempt, setLoadAttempt] = useState(0);

	useEffect(() => {
		if (!isLogged || loaded || hubLoading || hubErrorKey) return;

		dispatch(achievementsHubRequest());
	}, [isLogged, loaded, hubLoading, hubErrorKey, dispatch]);

	useEffect(() => {
		if (loaded) setLoadAttempt(0);
	}, [loaded]);

	useEffect(() => {
		if (!isLogged || loaded || hubLoading || !hubErrorKey) return;

		const backoff = Math.min(LOAD_RETRY_BASE_MS * 2 ** loadAttempt, LOAD_RETRY_MAX_MS);
		const timeout = setTimeout(
			() => {
				setLoadAttempt(attempt => attempt + 1);
				dispatch(achievementsHubRequest());
			},
			backoff * (0.8 + Math.random() * 0.4),
		);

		return () => clearTimeout(timeout);
	}, [isLogged, loaded, hubLoading, hubErrorKey, loadAttempt, dispatch]);

	useEffect(() => {
		if (!isLogged || !playerId || !username) return;

		const socket = getSocket(String(playerId), String(username));
		let handActive = false;
		let handTimer: ReturnType<typeof setTimeout> | undefined;
		let held: UnlockedEntry[] = [];

		const endHand = () => {
			handActive = false;
			if (handTimer) clearTimeout(handTimer);
			handTimer = undefined;

			const pending = held;
			held = [];
			showUnlockedToast(pending, familiesRef.current);
		};

		const startHand = () => {
			handActive = true;
			if (handTimer) clearTimeout(handTimer);
			handTimer = setTimeout(endHand, HAND_HOLD_LIMIT_MS);
		};

		const onUnlocked = (raw: unknown) => {
			const entries = toUnlockedEntries(raw);
			if (!entries.length) return;

			dispatch(achievementsHubRequest());

			if (handActive) {
				held = [...held, ...entries];
				return;
			}
			showUnlockedToast(entries, familiesRef.current);
		};

		const onAnyEvent = (event: string, payload?: unknown) => {
			if (event === UNLOCKED_EVENT) onUnlocked(payload);
			else if (HAND_START_EVENTS.has(event)) startHand();
			else if (HAND_END_EVENTS.has(event)) endHand();
		};

		socket.onAny(onAnyEvent);

		return () => {
			socket.offAny(onAnyEvent);
			if (handTimer) clearTimeout(handTimer);
		};
	}, [isLogged, playerId, username, dispatch]);

	return null;
};

export default AchievementsHost;
